import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EXPORTED_FILES_AFTER_ORIGINAL_DELETE,
  formatListTime,
  jobStatusLabel,
  originWord,
  type JobSearchFilter,
  type OriginalsDeleteResultItem,
} from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { Button, CheckerThumb, Skeleton } from "../components/primitives";
import { LimitBanner } from "../components/banners";
import { EmptyState, Toast } from "../components/feedback";
import {
  AppliedChips,
  FindConditions,
  NO_CONDITIONS,
  conditionCount,
  makeStatusLabeller,
} from "../features/filters/FindConditions";
import { listJobs } from "../lib/api";
import {
  bulkDeleteErrorMessage,
  bulkDeleteFailed,
  bulkDeleteRowTail,
  bulkDeleteSummary,
  deleteOriginals,
  fetchOriginalsSummary,
} from "../lib/originals-api";

const statusLabel = makeStatusLabeller();

/** 한 번에 받아 오는 줄 수. `W-20` 과 같은 값이고 같은 이유다. 다음 페이지가 없으면 이 값이
 *  곧 상한이 되어, 50건이 넘는 셀러의 오래된 작업은 어떤 조건으로도 이 목록에 못 나온다.
 *
 * ⛔ **`ORIGINALS_DELETE_MAX_JOBS`(50)를 넘기지 않는다.** 고를 수 있는 것은 지금 페이지의 행뿐이라
 * (`chosen`), 이 값이 곧 한 번에 지울 수 있는 건수의 상한이다. 그래서 화면에 50건 초과 갈래가 없다.
 * 이 값을 올리면 서버가 400 으로 거절한다. `scripts/web-console-annotations.test.mjs` 가 두 수를 잰다. */
const PAGE_SIZE = 50;

/** `W-55` 작업 이력 관리 · `W-55b` 찾기 조건 열린 상태.
 *
 * **마이페이지의 `작업 이력 관리 18건 ›`이 갈 곳이 없었다.** 꺾쇠는 "다른 화면으로 간다"는
 * 약속인데 그 화면이 없었으니 규칙을 어기고 있던 자리다(PRD 7.7.6a).
 *
 * **앱이 넷으로 나눈 것을 웹은 한 화면에 편다** — `A-44`(목록) · `A-45`(찾기 조건) ·
 * `A-46`(걸린 조건) · `A-47`(결과 없음)이 폭 안에 다 들어간다. 기능은 같고 조작 방식만
 * 다르다.
 *
 * **`W-55b` 를 별도 컴포넌트로 뜨지 않는다.** 목업이 프레임을 나눈 이유는 한 프레임에
 * 오버레이를 둘 두면 서로 덮기 때문이고, 구현에서는 한 번에 하나만 열면 그 문제가 없다. */
export function AccountJobsScreen() {
  const navigate = useNavigate();
  const [conditions, setConditions] = useState<JobSearchFilter>(NO_CONDITIONS);
  const [offset, setOffset] = useState(0);
  // 처음에는 아무것도 고르지 않는다. 지우는 목록이라 고른 상태로 열면 셀러가 고른 적 없는
  // 작업이 지울 대상으로 먼저 선다.
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [findOpen, setFindOpen] = useState<{ open: boolean; cal: boolean }>({
    open: false,
    cal: false,
  });

  /** 조건이 바뀌면 첫 페이지로 돌아간다(`W-20` 과 같다). */
  function applyConditions(next: JobSearchFilter) {
    setConditions(next);
    setOffset(0);
  }

  // **찾기 조건까지 서버로 보낸다**(`listJobs` 머리말). 화면에서 거르면 두 번째 페이지부터
  // 조건이 안 걸린 행이 섞인다. 질의 키 앞머리가 `W-20` 과 같아 업로드 뒤 무효화도 같이 받는다.
  const listQuery = useQuery({
    queryKey: ["jobs", conditions, offset],
    queryFn: () => listJobs(conditions, { limit: PAGE_SIZE, offset }),
  });

  const rows = listQuery.data?.items ?? [];
  const matchedCount = listQuery.data?.matchedCount ?? 0;
  const totalCount = listQuery.data?.totalCount ?? 0;
  const hasConditions = conditionCount(conditions) > 0;
  const shownTo = offset + rows.length;
  const paged = matchedCount > rows.length;

  const chosen = selected.filter((id) => rows.some((r) => r.id === id));

  // ── 남은 원본 ──────────────────────────────────────────────────────────
  //
  // 행의 `원본 n장` 은 **남은 원본**이다. 올린 장 전체(`imageTotalCount`)로 적으면 지운 뒤에도
  // `원본 9장` 이 남아 거짓이 된다. 서버 목록은 원본이 한 장이라도 남은 작업만 주므로, 목록이 잘리지
  // 않았는데 없는 작업은 `원본 없음` 이다.
  // ⛔ **모르는 수를 지어내지 않는다.** 불러오는 중 · 실패(다시 부르다 실패하면 React Query 가 옛 값을 들고
  // 있다) · 지우기가 끝나고 다시 부르는 중 · 목록이 잘렸는데 없는 작업은 그 칸을 통째로 뺀다(`W-50` 과 같다).
  const originalsQuery = useQuery({ queryKey: ["originals-summary"], queryFn: fetchOriginalsSummary });
  function remainingOriginals(jobId: string): number | null {
    if (!originalsQuery.isSuccess || deleteMutation.isPending) return null;
    const summary = originalsQuery.data;
    const hit = summary.items.find((i) => i.jobId === jobId);
    if (hit) return hit.originalCount;
    return summary.truncated ? null : 0;
  }

  // ── 고른 작업 지우기 ────────────────────────────────────────────────────
  //
  // 서버는 언제나 작업마다 결과를 준다. **일부 성공을 실패로 뭉개지 않는다.** 지우지 못한 행만 고른
  // 채로 남겨 같은 버튼으로 다시 누를 수 있게 하고, 그 행 끝에 사유를 단다.
  // 비용: 한 요청이 최대 50건 · D1 200 · R2 150 호출(DATA_DICTIONARY 51.4). 건수 상한은 `PAGE_SIZE` 가 진다.
  const queryClient = useQueryClient();
  const [results, setResults] = useState<Record<string, OriginalsDeleteResultItem>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  function showToast(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(null), 5000);
  }
  const deleteMutation = useMutation({
    mutationFn: (jobIds: string[]) => deleteOriginals(jobIds),
    onSuccess: (res) => {
      setResults(Object.fromEntries(res.items.map((i) => [i.jobId, i])));
      setSelected(res.items.filter(bulkDeleteFailed).map((i) => i.jobId));
      const summary = bulkDeleteSummary(res);
      if (summary.kind === "banner") {
        setBanner(summary.message);
      } else {
        setBanner(null);
        showToast(summary.message);
      }
    },
    onError: (error) => {
      setBanner(null);
      // `null` 은 401 이다. 세션 게이트가 로그인으로 넘기므로 알림을 띄우지 않는다.
      const message = bulkDeleteErrorMessage(error);
      if (message !== null) showToast(message);
    },
    // ⭐ **다시 부르기를 기다린다**(`await`). 그동안 `isPending` 이라 행이 지우기 전 장 수를 그리지 않는다.
    // 고른 작업마다 `W-50` 의 원본 현황(`job-originals`)과 작업(`job`)도 다시 부른다. 다른 화면에 옛 수가 남지 않게.
    onSettled: async (_res, _error, jobIds) => {
      setConfirmDelete(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["originals-summary"] }),
        ...jobIds.flatMap((id) => [
          queryClient.invalidateQueries({ queryKey: ["job-originals", id] }),
          queryClient.invalidateQueries({ queryKey: ["job", id] }),
        ]),
      ]);
    },
  });

  function toggleOne(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }

  return (
    <>
      <PageHead
        frame="W-55"
        title="작업 이력 관리"
        subtitle="지난 작업을 찾고 골라 지웁니다"
        right={
          listQuery.isSuccess ? `조건에 맞는 ${matchedCount}건 · 전체 ${totalCount}건 중` : undefined
        }
      />

      <p className="hint" style={{ margin: "16px 0 9px" }}>
        <b>기간 · 상태 · 올린 곳</b>으로 좁힐 수 있습니다. 기간은 하나만, 나머지는 여러 개를
        고릅니다.
      </p>

      <div
        className={`tablebar${findOpen.open ? " dropopen" : ""}${findOpen.cal ? " cal" : ""}`}
      >
        <AppliedChips applied={conditions} onChange={applyConditions} statusLabel={statusLabel} />
        <FindConditions
          applied={conditions}
          onApply={applyConditions}
          onOpenChange={(open, cal) => setFindOpen({ open, cal })}
        />
      </div>

      {/* 하나라도 지우지 못했으면 다음 동작까지 남는다. 사유는 그 줄 끝에 있다. */}
      {banner ? <LimitBanner>{banner}</LimitBanner> : null}

      {/* ⭐ `.countline` 은 짧은 것과 줄글이 섞인 자리다. `Jobs.tsx` 의 둘은 `조건에 맞는 N건 ·
          전체 M건 중` 처럼 이름과 값이고, 아래 하나만 세 문장 57자 줄글이다. 그래서 선택자를
          늘리지 않고 여기에만 `prose` 를 붙인다(`styles/base.css`). */}
      {listQuery.isPending ? (
        /* 불러오는 중을 "없다"로 말하지 않는다(`W-20` 과 같다). */
        <div role="status" aria-live="polite" style={{ margin: "16px 0" }}>
          <span className="sr-only">내 작업을 불러오는 중입니다</span>
          <div style={{ display: "grid", gap: 9 }}>
            <Skeleton width="34%" height={13} />
            <Skeleton width="88%" />
            <Skeleton width="72%" />
          </div>
        </div>
      ) : listQuery.isError ? (
        /* 조회가 실패한 것과 작업이 없는 것은 다른 사실이다. 문구는 `W-20` 의 것 그대로다. */
        <EmptyState
          art="!"
          tone="bad"
          title="작업 목록을 지금 불러올 수 없어요"
          body={
            <>
              연결이 끊겼거나 잠깐 답이 오지 않았습니다. <b>작업이 없어진 것이 아닙니다.</b>
            </>
          }
          action={<Button label="다시 시도" onClick={() => void listQuery.refetch()} />}
        />
      ) : rows.length === 0 && !hasConditions ? (
        /* 조건이 없는데 비었으면 "조건이 좁다"가 거짓이다. 작업이 아예 없는 것이다. 문구는
           `W-20` 의 빈 상태 첫 줄이고, 다음 행동은 그 화면으로 가는 것이다. */
        <EmptyState
          art="▤"
          title="아직 작업이 없습니다"
          body="폰에서 올린 것이 여기 그대로 나타납니다."
          action={<Button label="내 작업" onClick={() => navigate("/jobs")} />}
        />
      ) : rows.length === 0 ? (
        <p className="countline prose">
          이 조건에 맞는 게 없습니다. 작업이 없는 게 아니라 조건이 좁습니다. 칩의{" "}
          <span className="mono">✕</span>로 하나씩 풀어 보세요.
        </p>
      ) : (
        <div className="orderlist" style={{ gap: 0 }}>
          {rows.map((row) => {
            const on = selected.includes(row.id);
            // 상품명이 없으면 작업 번호를 대신 쓰지 않는다(PRD 7.5.9 규칙 3. `W-20` 과 같은 말).
            const name = row.productName ?? "이름 없는 작업";
            // 출처를 모르는 작업(`UNKNOWN`)은 `null` 이다. 「웹」이라 적지 않고 꼬리를 뗀다.
            const origin = originWord(row.origin);
            const remaining = remainingOriginals(row.id);
            const result = results[row.id];
            const tail = result ? bulkDeleteRowTail(result) : null;
            return (
              <div className={`selrow${on ? " on" : ""}`} key={row.id}>
                {/* 한 행에 동작이 둘이라 체크박스만 별도 히트 영역을 갖는다(PRD 7.4 예외). */}
                <button
                  type="button"
                  className="cb hit44"
                  role="checkbox"
                  aria-checked={on}
                  aria-label={`${name} 고르기`}
                  onClick={() => toggleOne(row.id)}
                >
                  ✓
                </button>
                <CheckerThumb size={30} />
                <button
                  type="button"
                  className="open"
                  onClick={() => navigate(`/jobs/${row.id}/history`)}
                >
                  <span>
                    <span className="sn">{name}</span>
                    <span className="sd mono">
                      {/* 줄 모양은 목업 `W-55` 그대로다. `날짜 · 상태 · 원본 n장 · 올린 곳`. 남은 원본이 0이면
                          `원본 없음`(W-50 과 같은 말). 지우고 난 뒤 결과 꼬리가 끝에 붙는다(`A-22` 행 사유 모양). */}
                      {formatListTime(row.createdAt)} · {jobStatusLabel(row.status).label}
                      {remaining === null ? "" : remaining === 0 ? " · 원본 없음" : ` · 원본 ${remaining}장`}
                      {origin ? ` · ${origin}` : ""}
                      {tail ? ` · ${tail}` : ""}
                    </span>
                  </span>
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {listQuery.isSuccess && paged ? (
        <div className="actions" style={{ marginBottom: 12 }}>
          <Button
            label="‹ 이전"
            variant="ghost"
            small
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          />
          <Button
            label="다음 ›"
            variant="ghost"
            small
            disabled={shownTo >= matchedCount}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          />
          <span className="why">한 번에 {PAGE_SIZE}건씩 보여줍니다. 최신순 고정입니다.</span>
        </div>
      ) : null}

      <div className={`actions${confirmDelete ? " dropopen" : ""}`}>
        {/* 트리거는 `ghost danger`, 확인 창 안의 마지막 버튼만 채움이다. 트리거까지 채우면
            목록 화면이 "지우기"로 먼저 읽힌다(PRD 7.5.8). */}
        <span className="dropwrap">
          <Button
            label={`${chosen.length}건의 원본 사진 지우기`}
            variant="ghost-danger"
            small
            disabled={chosen.length === 0 || deleteMutation.isPending}
            aria-expanded={confirmDelete}
            onClick={() => setConfirmDelete((v) => !v)}
          />
          {confirmDelete ? (
            <span className="webdrop fromleft" role="group" aria-label="지우기 확인">
              <span className="dh">되돌릴 수 없습니다</span>
              {/* 문구는 앱의 확인 시트와 같은 말이다. 파괴적 확인은 자리마다 다르게 말하면
                  안 된다. 두 문장 58자 줄글이라 어절 보존을 걷는다(`styles/base.css` 의
                  `.prose`). 같은 말이 폰에도 있으니 한쪽만 걷지 않는다. */}
              {/* 둘째 문장은 2026-10-13 정정(PRD `7.6.14c`) 전의 「이미 내보낸 파일과 청구 내역은 그대로
                  남습니다」를 바꾼 것이다. 결과에 기간이 생겨 「그대로 남는다」가 거짓이 됐다. 문장은 shared
                  한 벌이고 여러 작업이라 `EACH_JOB` 이다(2026-10-25 `design-manager` 자문). */}
              <span className="dn prose">
                지우면 그 {chosen.length}건은 <b>이전 판으로 되돌릴 수 없게</b> 됩니다.{" "}
                {EXPORTED_FILES_AFTER_ORIGINAL_DELETE.EACH_JOB} 청구 내역은 지워지지 않습니다.
              </span>
              <span className="drow">
                <Button
                  label="그대로 두기"
                  variant="ghost"
                  small
                  onClick={() => setConfirmDelete(false)}
                />
                <Button
                  label={`${chosen.length}건 지우기`}
                  variant="danger"
                  small
                  disabled={deleteMutation.isPending || chosen.length === 0}
                  onClick={() => deleteMutation.mutate(chosen)}
                />
              </span>
            </span>
          ) : null}
        </span>
        <Button label="선택 해제" variant="ghost" small onClick={() => setSelected([])} />
        <span className="why">
          한 줄을 누르면 그 작업의 이력이 열립니다. 왼쪽 네모를 누르면 골라 지웁니다.
        </span>
      </div>

      {/* ⛔ **설계 메모 상자를 걷었다** (2026-10-04 사용자 지시). 「목적이 달라 화면이 둘입니다」
          콜아웃, 「고르기와 확인 창까지는 실제로 동작합니다」 배너, 「이 화면에서 아직 못 합니다」 둘
          (그중 하나는 `W-55b` 화면 번호를 달고 있었다)은 설계자에게 하는 말이었다. 지운 결과는 알림 ·
          배너 · 행 꼬리가 말한다. ⛔ 옛 비활성 사유 「보관 정책이 정해지기 전」은 걷었다(O-9 닫힘). */}
      {toast ? <Toast message={toast} /> : null}
    </>
  );
}
